/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo } from "react";

// antd:
// import { LoadingOutlined } from "@ant-design/icons";
// import { Spin } from "antd";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
import DivE from "../react/DivE.tsx";
// Redux-hooks:
import { useAppSelector } from "../../libs/redux-hooks.ts"; // useAppDispatch
// Reducers:
import { getAppState, getTheme, getUser } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

const { rz } = window;

// const spinIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

const Grid:FC<TuniversalElementProps> = ({attributes}) => {
  Grid.displayName = "Grid";
  const user = useAppSelector(getUser);
  const theme = useAppSelector(getTheme);

  const {rootAttributes, key, fullPath, content} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key)), elState = appState[fullPath]; // value, visible, disabled, loading, userStyle, userClass
  // XML задает стартовые layout-атрибуты, а обработчики могут менять их через Redux.
  // Объединяем оба источника, чтобы childWidth, type, masonry, parallax и gap/gutter обновляли grid без перезагрузки XML.
  const gridAttributes = useMemo(() => ({...attributes, ...elState.attributes}), [attributes, elState.attributes]);
  const {masonry, parallax, type, childWidth, gap, gutter} = gridAttributes;
  const {color, backgroundColor, borders} = elState.attributes;
  // const rootNamespace = useMemo(() => (window[rootAttributes?.key || rz.strMakeID(24)]), [rootAttributes?.key]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, gridAttributes)), [user, gridAttributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    if (!~userClss.indexOf("uk-grid")) { userClss.push("uk-grid") }
    if (!rz.arrCompare(userClss, ["uk-grid-small", "uk-grid-medium", "uk-grid-large", "uk-grid-collapse"], "includesAny")) { userClss.push("uk-grid-collapse") }
    if (type) {
      if (!~userClss.indexOf("uk-flex")) { userClss.push("uk-flex") }
      if (type === "vertical" && !~userClss.indexOf("uk-flex-column")) { userClss.push("uk-flex-column") }
      if (type === "horizontal" && !~userClss.indexOf("uk-flex-row")) { userClss.push("uk-flex-row") }
    }
    if (childWidth) {
      const cwidth = rz.arrGetUnique((childWidth || "rz-child-widh-auto").split(" "), true, true, true);
      cwidth.forEach((ww) => {
        if ((/^(\d-\d)|(\d{1,2}em)/igm).test(ww)) { userClss.push("rz-child-width-" + ww) }
      });
      // if (!rz.arrContains(userClss, /@xs$/igm)) { userClss.push("rz-width-1-1@xs") }
    }
    if (borders && !rz.arrContains(userClss, /^rz-borders-/igm)) {
      borders.split(" ").filter((ff) => ((/^(light|dark|l1|l2|l3|l4|l5|d1|d2|d3|d4|d5|hover)$/igm).test(ff)))
        .forEach((ff:string) => { if (!rz.arrContains(userClss, "rz-borders-" + ff)) { userClss.push("rz-borders-" + ff) } });
    }
    return userClss;
  }, [elState?.userClass, elState?.disabled, type, childWidth, borders, permissions]);

  // uk-grid-match - высота всех ячеек одинакова (подобрана по содержимому самой большой ячейки). Класс размещается в grid
  // uk-grid-item-match - тоже самое, но этот класс размещается в дочерних ячейках, а не в родительской grid
  // uk-height-match="target: > div > .uk-card"
  // rz-child-width-1-2@s   rz-child-width-1-3@m - применимо только к grid
  // rz-width-auto   rz-width-1-3@m   rz-width-expand@s - применимо только к дочерним элементам grid
  // uk-flex-center   uk-flex-middle
  // uk-grid="masonry: true"

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor && bclr) { est.backgroundColor = bclr }
    // gap/gutter управляют реальным расстоянием между cell.
    // Это надежнее, чем UIkit uk-grid-* классы, которые в текущей сборке не дают горизонтальный отступ.
    const rawGap = gutter ?? gap;
    const gapByName:{[key:string]:string} = {collapse: "0px", small: "8px", medium: "16px", large: "24px"};
    if (rawGap || rawGap === 0) {
      const gapValue = String(rawGap);
      est.gap = gapByName[gapValue] || (rz.canbeNumber(gapValue) ? `${gapValue}px` : gapValue);
    }
    return est;
  }, [elState?.userStyle, elState?.visible, color, backgroundColor, theme, permissions, gutter, gap]);

  return (
    <DivE key={key} id={key} className={clsu.join(" ")} style={styl} uk-grid={(masonry ? "; masonry:true" : "") + " " + (parallax ? "; parallax:" + parallax : "")}>
      {content.map((cell) => {
        const cState = appState[cell.fullPath], cellstyle = {...cState.userStyle}, cellclass = rz.objClone(cState?.userClass || []);
        // Атрибуты cell тоже объединяем с Redux, чтобы width, align и borders реагировали на обработчики.
        const cellAttributes = {...cell.attributes, ...cState.attributes};
        const cpermissions = fnc.checkPermissions(user, {...cellAttributes, rootAttributes: {key: rootAttributes.key}});
        if ((cState?.visible === false || cpermissions.visible === false) && cellstyle.display !== "none") { cellstyle.display = "none" }
        else if ((cState?.visible === true && cpermissions.visible !== false) && cellstyle.display === "none") { cellstyle.display = undefined }
        if ((cState?.disabled || cpermissions.disabled === true) && !~cellclass.indexOf("uk-disabled")) { cellclass.push("uk-disabled") }
        if (!rz.arrContains(cellclass, /^rz-padding/igm)) { cellclass.push("rz-padding-5") }
        if (!childWidth) {
          if (!rz.arrContains(cellclass, /^rz-width/igm) && !cellAttributes.width) { cellclass.push("rz-width-auto") }
          else if (!rz.arrContains(cellclass, /^rz-width/igm) && cellAttributes.width) {
            const cwidth = rz.arrGetUnique((cellAttributes.width || "").split(" "), true, true, true);
            cwidth.forEach((ww) => {
              if ((/^(\d-\d)|(\d{1,2}em)|expand|auto/igm).test(ww)) { cellclass.push("rz-width-" + ww) }
            });
          }
          // if (!rz.arrContains(cellclass, /@xs$/igm)) { cellclass.push("rz-width-1-1@xs") }
        }
        if (cellAttributes?.borders && !rz.arrContains(cellclass, /^rz-borders-/igm)) {
          cellAttributes.borders.split(" ").filter((ff:string) => ((/^(light|dark|l1|l2|l3|l4|l5|d1|d2|d3|d4|d5|hover)$/igm).test(ff)))
            .forEach((ff:string) => { if (!rz.arrContains(cellclass, "rz-borders-" + ff)) { cellclass.push("rz-borders-" + ff) } });
        }
        if (cellAttributes?.align) {
          if (!~cellclass.indexOf("uk-flex")) { cellclass.push("uk-flex") }
          cellAttributes.align.toLowerCase().replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt:string) => {
            if ((/^(left|center|right|between|around|stretch|top|middle|bottom|row|column|wrap|nowrap|first|last|none|auto|\d+)/igm).test(tt) && !~cellclass.indexOf("uk-flex-" + tt)) {
              cellclass.push("uk-flex-" + tt);
            }
          });
        }

        return (
          <div key={cell.key} id={cell.key} className={cellclass.join(" ")} style={cellstyle}>
            {(cell.content || []).map((el) => (el.children))}
          </div>
        )
      })}
    </DivE>
  );
}

export default Grid;
